import { selectCodeEditorOutput, selectFetchingCodeEditorOutput } from '../../../../../store/exam/selectors';
import { Dispatch, forwardRef, SetStateAction, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, PlayIcon } from '../../../../../assets/icons/common';
import { runCodeEditorSnippetThunk } from '../../../../../store/exam/thunks';
import { useDispatch, useSelector } from '../../../../../store/store';
import { CodingExamQuestion } from '../../../../../store/exam/types';
import { useProctoring } from '../../../../../hooks/useProctoring';
import { toTime } from '../../../../../utils/generalHelpers';
import Button from '../../../../../components/Button';
import type * as monaco from 'monaco-editor';
import Editor from '@monaco-editor/react';
import styles from './style.module.scss';
import Webcam from 'react-webcam';

interface Props {
    isPaused: boolean;
    examId: string;
    counter: number;
    userId: string | null;
    questionCount: number;
    startWebcam: () => void;
    examData: CodingExamQuestion | null;
    setAnswer: Dispatch<SetStateAction<string>>;
};

const Exam = forwardRef<Webcam, Props>((props, webcamRef) => {
    const {
        isPaused,
        userId,
        examId,
        counter,
        examData,
        setAnswer,
        startWebcam,
        questionCount,
    } = props;

    const dispatch = useDispatch();
    const { hours, minutes, seconds } = toTime(counter);
    const [showMore, setShowMore] = useState(true);
    const output = useSelector(selectCodeEditorOutput);
    useProctoring(userId, examId, questionCount, 'codingExam', isPaused);
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
    const running = useSelector(selectFetchingCodeEditorOutput);

    useEffect(() => {
        startWebcam()
    }, []);

    useEffect(() => {
        if (!examData) return;
        clearEditor();
    }, [examData]);

    function handleEditorDidMount(editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco) {
        editorRef.current = editor;
    }

    const fileName = () => {
        switch (examData?.language) {
            case 'javascript':
                return 'main.js'
            case 'python':
                return 'main.py'
            case 'go':
                return 'main.go'
            case 'cpp':
                return 'main.cpp'
            case 'c':
                return 'main.c'
            case 'csharp':
                return 'main.cs'
            default:
                return ''
        }
    };

    function runCode() {
        if (!examData || !editorRef.current) return;
        const data = JSON.stringify({
            language: examData.language,
            files: [
                {
                    name: fileName(),
                    content: editorRef.current.getValue()
                }
            ]
        });

        dispatch(runCodeEditorSnippetThunk(data))
    }

    function clearEditor() {
        if (editorRef.current) {
            editorRef.current.setValue('');
        }
    }

    function handleEditorChange(value: string | undefined) {
        setAnswer(value || '');
    }

    return (
        <div className={styles.container}>
            <div className={styles.content} data-show-more={showMore}>
                <div className={styles.top_section}>
                    <div className={styles.question}>
                        <p><b>Question {questionCount}: </b>{examData?.description}</p>
                        {examData?.examples.map((i, indx) =>
                            <div className={styles.example}>
                                <span className={styles.example_title}>
                                    Example {indx + 1}:
                                </span>
                                <p>{i}</p>
                            </div>)}
                        <div className={styles.actions}>
                            <div
                                data-show-more={showMore}
                                className={styles.show_more}
                                onClick={() => setShowMore(prev => !prev)}
                            >
                                <span>{showMore ? 'Less' : 'More'}</span>
                                <ChevronDownIcon stroke='#FFFFFF' />
                            </div>
                        </div>
                    </div>
                    <div className={styles.stream}>
                        <div className={styles.stream_video}>
                            <Webcam
                                audio
                                muted
                                ref={webcamRef}
                            />
                        </div>
                        <div className={styles.stream_detail} data-show-more={showMore}>
                            <span className={styles.stream_detail_live} />
                            <span className={styles.stream_detail_countdown}>
                                {`${hours}:${minutes}:${seconds}`}
                            </span>
                        </div>
                    </div>
                </div>
                <div className={styles.bottom_section}>
                    <div className={styles.bottom_section_header}>
                        <span className={styles.bottom_section_header_title}>{fileName()}</span>
                        <Button
                            text='Run'
                            type='Error'
                            onClick={runCode}
                            loading={running}
                            icon={<PlayIcon />}
                            className={styles.bottom_section_header_btn}
                        />
                    </div>
                    <div className={styles.bottom_section_editor}>
                        <Editor
                            theme='vs-dark'
                            defaultValue=""
                            onChange={handleEditorChange}
                            onMount={handleEditorDidMount}
                            className={styles.code_editor}
                            defaultLanguage={examData?.language}
                            height={showMore ? 'calc(100vh - 472px)' : 'calc(100vh - 302px)'}
                        />
                        <div className={styles.bottom_section_editor_output}>
                            <p>
                                Output:<br /><br />
                                {output}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
});

export default Exam;